import { Icon, IconTypes } from '@eika/icon';
import { format } from '@translation-utils/use-translation';
import { useTranslation } from '@translations';
import type { ReactNode } from 'react';
import { ChildCardContext, type ChildCardContextType, useChildCardContext } from './context';
import './child-card.scss';
import type { AccountForChildEligibilityDetails } from '@generated-types';
import {
    CompleteKYCButton,
    CreateNewAskProcessButton,
    GiveConsentButton,
    GoToCompleteProcessStatusButton,
    GoToProcessStatusButton,
    StartSavingButton,
} from './actions/child-card-actions';
import { ChildCardEligibilityStatus } from './eligibility-status/child-card-eligibility-status';
import { ChildCardPendingTag } from './pending-tag/child-card-pending-tag';
import { actionAvailable } from './utils';

interface ChildCardProps {
    childEligibility: AccountForChildEligibilityDetails;
    children: ReactNode;
}

export const ChildCard = ({ childEligibility, children }: ChildCardProps) => {
    const { childCard } = useTranslation();

    const contextValue: ChildCardContextType = {
        childEligibility,
    };

    return (
        <ChildCardContext.Provider value={contextValue}>
            <li
                aria-label={format(childCard.ariaLabel.childElement, childEligibility.child.name)}
                className="child-card"
                data-testid="child-card"
            >
                <div className="child-card__icon-with-content">
                    <Icon aria-hidden className="icon--36 child-card__icon" type={IconTypes.UNG_36} />
                    {children}
                </div>
            </li>
        </ChildCardContext.Provider>
    );
};

const ChildCardName = () => {
    const { childEligibility } = useChildCardContext();

    return (
        <h3 id="child-name" className="child-card__name">
            {childEligibility.child.name}
        </h3>
    );
};

const ChildCardActions = () => {
    const { childEligibility } = useChildCardContext();

    if (!actionAvailable(childEligibility)) {
        return null;
    }

    return (
        <div className="child-card__actions">
            <CreateNewAskProcessButton />
            <GiveConsentButton />
            <CompleteKYCButton />
            <StartSavingButton />
            <GoToProcessStatusButton />
            <GoToCompleteProcessStatusButton />
        </div>
    );
};

ChildCard.Name = ChildCardName;
ChildCard.Tag = ChildCardPendingTag;
ChildCard.EligibilityStatus = ChildCardEligibilityStatus;
ChildCard.Actions = ChildCardActions;
